import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Paper,
  Avatar,
  Stack,
  Snackbar,
  Alert,
  Fade,
  Zoom,
  Divider,
  IconButton,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SendIcon from '@mui/icons-material/Send';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ShieldIcon from '@mui/icons-material/Shield';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import DiamondIcon from '@mui/icons-material/Diamond';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#b8942e',
};

const Home = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeFlagIndex, setActiveFlagIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const flagsPerPage = 6;

  const flagCountries = [
    { name: "United States", flag: "/images/american flag.jpg", code: "US" },
    { name: "United Kingdom", flag: "/images/unitedkingdomflag.jpg", code: "UK" },
    { name: "Germany", flag: "/images/Germany.jpg", code: "DE" },
    { name: "France", flag: "/images/france.jpg", code: "FR" },
    { name: "Australia", flag: "/images/Australian.jpg", code: "AU" },
    { name: "Canada", flag: "/images/Canada.png", code: "CA" },
    { name: "Brazil", flag: "/images/brazil.jpg", code: "BR" },
    { name: "India", flag: "/images/India.png", code: "IN" },
    { name: "South Africa", flag: "/images/south africa.png", code: "ZA" },
    { name: "Italy", flag: "/images/italy.png", code: "IT" },
    { name: "Spain", flag: "/images/spain.jpg", code: "ES" },
    { name: "Argentina", flag: "/images/Argentina.jpg", code: "AR" },
    { name: "European Union", flag: "/images/Europe.jpg", code: "EU" },
  ];

  const officeImages = [
    "/images/OUROFFICESimage1.png",
    "/images/OUROFFICESimage2.png",
    "/images/OUROFFICESimage3.png",
    "/images/OUROFFICESimage4.png",
    "/images/OUROFFICESimage5.png",
    "/images/OUROFFICESimage6.png",
    "/images/OUROFFICESimage7.png",
    "/images/Mastercard’s Success.jpg",
  ];

  // Only two news articles
  const newsItems = [
    {
      title: "4 Expenses You Should Never Put on Your Business Credit Card",
      excerpt: "Although the majority of credit cards in circulation are for consumers, the popularity of business credit cards is on the upswing. According to a 2018 report by the Mercator Advisory Group, business credit card usage has grown significantly...",
      image: "/images/Credit Card.jpg",
      date: "December 15, 2024",
    },
    {
      title: "The Secret to Mastercard's Success",
      excerpt: "For investors, the ideal stock would probably be one that operates in a growing market, has a sustainable competitive advantage, and faces little competition. Enter Mastercard (NYSE:MA). The credit card giant has transformed the way the world makes payments...",
      image: "/images/Mastercard’s Success.jpg",
      date: "December 10, 2024",
    },
  ];

  const benefits = [
    { icon: <ShieldIcon sx={{ fontSize: 24 }} />, title: "Bank-Grade Security", desc: "Military-grade encryption protecting your wealth" },
    { icon: <WorkspacePremiumIcon sx={{ fontSize: 24 }} />, title: "Premium Benefits", desc: "Exclusive rewards and lifestyle privileges" },
    { icon: <TrendingUpIcon sx={{ fontSize: 24 }} />, title: "Wealth Growth", desc: "Interest-bearing accounts and investment options" },
  ];

  // Auto-slideshow effect
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setActiveFlagIndex((prevIndex) => 
          prevIndex + flagsPerPage >= flagCountries.length ? 0 : prevIndex + flagsPerPage
        );
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, flagCountries.length, flagsPerPage]);

  const displayedFlags = flagCountries.slice(activeFlagIndex, activeFlagIndex + flagsPerPage);
  const totalPages = Math.ceil(flagCountries.length / flagsPerPage);
  const currentPage = Math.floor(activeFlagIndex / flagsPerPage);

  const handleFlagNext = () => {
    setIsAutoPlaying(false);
    setActiveFlagIndex((prevIndex) => 
      prevIndex + flagsPerPage >= flagCountries.length ? 0 : prevIndex + flagsPerPage
    );
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleFlagBack = () => {
    setIsAutoPlaying(false);
    setActiveFlagIndex((prevIndex) => 
      prevIndex - flagsPerPage < 0 ? Math.max(0, flagCountries.length - flagsPerPage) : prevIndex - flagsPerPage
    );
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setSnackbar({ open: true, message: 'Thank you! Our concierge will contact you within 24 hours.', severity: 'success' });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Box>
      {/* Hero Section - Enhanced with reduced opacity and better visibility */}
      <Box 
        sx={{ 
          position: 'relative',
          height: { xs: '85vh', md: '90vh' },
          minHeight: { xs: 600, md: 700 },
          backgroundImage: 'url("/images/homepagehero.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13,17,23,0.4) 0%, rgba(26,26,46,0.3) 100%)',
          }
        }}
      >
        {/* Animated background particles effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0,
          }}
        >
          {[...Array(20)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: 2,
                height: 2,
                backgroundColor: `${colors.premiumGold}`,
                opacity: 0.3,
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${5 + Math.random() * 10}s infinite ease-in-out`,
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px) translateX(0px)', opacity: 0.3 },
                  '50%': { transform: `translateY(${-20 + Math.random() * 40}px) translateX(${-10 + Math.random() * 20}px)`, opacity: 0.8 },
                }
              }}
            />
          ))}
        </Box>

        {/* Top Section - Text with enhanced animations */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', pt: { xs: 12, md: 16 } }}>
          <Fade in timeout={1000}>
            <Box>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '5rem' }, 
                  mb: 3, 
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${colors.premiumGold} 0%, ${colors.lightGold} 50%, ${colors.gold} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  textShadow: '0 0 40px rgba(185,148,46,0.3)',
                  letterSpacing: -0.5,
                  animation: 'glow 2s ease-in-out infinite',
                  '@keyframes glow': {
                    '0%, 100%': { textShadow: '0 0 20px rgba(185,148,46,0.3)' },
                    '50%': { textShadow: '0 0 40px rgba(185,148,46,0.6)' },
                  }
                }}
              >
                YOUR OWN PRIVATE BANK
              </Typography>
              
              <Typography 
                variant="h2" 
                sx={{ 
                  color: colors.lightGold, 
                  fontWeight: 600, 
                  fontSize: { xs: '1.5rem', md: '2.5rem' },
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  borderTop: `2px solid ${colors.premiumGold}60`,
                  borderBottom: `2px solid ${colors.premiumGold}60`,
                  display: 'inline-block',
                  px: 4,
                  py: 1.5,
                  backdropFilter: 'blur(8px)',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: 4,
                }}
              >
                THE SAFE BANK ®
              </Typography>
            </Box>
          </Fade>
        </Container>

        {/* Bottom Section - Buttons with hover effects */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', pb: { xs: 8, md: 12 } }}>
          <Stack 
            direction="row" 
            spacing={4} 
            justifyContent="center"
            sx={{
              '& > button': {
                animation: 'fadeInUp 0.8s ease-out',
                '@keyframes fadeInUp': {
                  from: { opacity: 0, transform: 'translateY(30px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                }
              }
            }}
          >
            <Button 
              variant="contained" 
              href="#about"
              sx={{ 
                bgcolor: colors.premiumGold,
                color: colors.navy,
                fontWeight: 800, 
                px: 5, 
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 50,
                boxShadow: `0 4px 15px ${colors.premiumGold}60`,
                '&:hover': { 
                  bgcolor: colors.lightGold, 
                  transform: 'translateY(-3px)', 
                  boxShadow: `0 10px 30px ${colors.premiumGold}80`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              About Us
            </Button>
            <Button 
              variant="outlined" 
              href="#contact"
              sx={{ 
                borderColor: colors.premiumGold,
                borderWidth: 2,
                color: colors.white,
                fontWeight: 800, 
                px: 5, 
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 50,
                '&:hover': {
                  borderColor: colors.lightGold,
                  backgroundColor: `${colors.premiumGold}1A`,
                  transform: 'translateY(-3px)',
                  boxShadow: `0 8px 25px ${colors.premiumGold}40`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Contact Us
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Diamond Card Locations - Premium Dark Background with Auto Slideshow */}
      <Box sx={{ bgcolor: colors.premiumDark, py: 5, borderTop: `1px solid ${colors.premiumGold}33`, borderBottom: `1px solid ${colors.premiumGold}33` }}>
        <Container maxWidth="lg">
          <Typography variant="h4" textAlign="center" sx={{ fontWeight: 700, color: colors.premiumGold, mb: 4, letterSpacing: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            DIAMOND CARD IS AT
          </Typography>
          
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: 3,
              mb: 3
            }}>
              <IconButton 
                onClick={handleFlagBack} 
                sx={{ 
                  color: colors.premiumGold,
                  border: `1px solid ${colors.premiumGold}33`,
                  backgroundColor: 'rgba(185,148,46,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': { 
                    backgroundColor: `${colors.premiumGold}1A`,
                    transform: 'scale(1.1)',
                    borderColor: colors.premiumGold
                  }
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
              
              <IconButton 
                onClick={handleFlagNext}
                sx={{ 
                  color: colors.premiumGold,
                  border: `1px solid ${colors.premiumGold}33`,
                  backgroundColor: 'rgba(185,148,46,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': { 
                    backgroundColor: `${colors.premiumGold}1A`,
                    transform: 'scale(1.1)',
                    borderColor: colors.premiumGold
                  }
                }}
              >
                <KeyboardArrowRight />
              </IconButton>
            </Box>
            
            <Grid container spacing={3} justifyContent="center">
              {displayedFlags.map((country, idx) => (
                <Zoom in timeout={300 + idx * 100} key={country.code}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Box
                        component="img"
                        src={country.flag}
                        alt={`${country.name} flag`}
                        sx={{
                          width: 100,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 2,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                          mb: 1.5,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'scale(1.08) rotate(2deg)',
                            boxShadow: `0 8px 25px ${colors.premiumGold}60`,
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: colors.white, display: 'block', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                        {country.name}
                      </Typography>
                    </Box>
                  </Grid>
                </Zoom>
              ))}
            </Grid>
            
            {/* Page Indicators */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 1.5 }}>
              {Array.from({ length: totalPages }).map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setActiveFlagIndex(idx * flagsPerPage);
                    setTimeout(() => setIsAutoPlaying(true), 5000);
                  }}
                  sx={{
                    width: currentPage === idx ? 20 : 6,
                    height: 6,
                    borderRadius: currentPage === idx ? '3px' : '50%',
                    backgroundColor: currentPage === idx ? colors.premiumGold : `${colors.white}33`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: colors.premiumGold,
                      width: 20,
                      borderRadius: '3px',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* About Section - Pearl White with enhanced design */}
      <Box sx={{ py: 12, bgcolor: colors.pearl }} id="about">
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Chip 
                  label="ABOUT US" 
                  sx={{ 
                    bgcolor: colors.premiumGold, 
                    color: colors.navy, 
                    fontWeight: 800,
                    px: 4,
                    py: 2,
                    fontSize: '0.9rem',
                    letterSpacing: 2,
                    mb: 4,
                    '&:hover': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.3s',
                    }
                  }}
                />
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontSize: { xs: '2rem', md: '3.5rem' }, 
                    mb: 2, 
                    color: colors.navy, 
                    fontWeight: 800,
                    letterSpacing: -0.5,
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 60,
                      height: 3,
                      bgcolor: colors.premiumGold,
                      borderRadius: 2,
                    }
                  }}
                >
                  DIAMOND CARD CORPORATION
                </Typography>
                <Typography 
                  variant="h5" 
                  color={colors.premiumGold} 
                  sx={{ fontWeight: 600, fontSize: '1.3rem', mt: 3 }}
                >
                  The First Choice Alternate Bank
                </Typography>
              </Box>

              <Paper elevation={0} sx={{ 
                p: { xs: 4, md: 7 }, 
                borderRadius: 4, 
                boxShadow: `0 25px 50px ${colors.navy}1A`,
                bgcolor: colors.white,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${colors.premiumGold}, ${colors.lightGold}, ${colors.premiumGold})`,
                }
              }}>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: colors.textDark, textAlign: 'center' }}>
                  Diamond Card Corporation provides services worldwide. We have established the Diamond Network, 
                  allowing merchants and service providers to perform financial transactions when their consumers 
                  present a Diamond card.
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: colors.textDark, textAlign: 'center' }}>
                  Initial rollout of the Diamond Card is in the United States; any organization, merchant, or community 
                  is welcome to issue personalised prepaid, debit, or credit cards to their client base or members. 
                  Transactions made within the community carry no fees. Payments and transfers can be made between 
                  community members at no cost.
                </Typography>
                <Divider sx={{ my: 4 }} />
                <Typography variant="body1" sx={{ 
                  fontSize: '1.25rem', 
                  lineHeight: 1.8, 
                  color: colors.premiumGold, 
                  fontWeight: 700, 
                  fontStyle: 'italic', 
                  textAlign: 'center',
                  backgroundColor: `${colors.premiumGold}0D`,
                  p: 3,
                  borderRadius: 3,
                }}>
                  "Be your own private bank with online access and our Mobile APP. All of our cards also include the Tap feature for payment ease."
                </Typography>
              </Paper>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Benefits Section - 3 cards in one row with reduced size */}
      <Box 
        sx={{ 
          position: 'relative',
          py: 10,
          backgroundImage: 'url("/images/herobackground.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${colors.premiumDark}EE 0%, ${colors.charcoal}DD 100%)`,
            backdropFilter: 'blur(5px)',
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" textAlign="center" sx={{ fontWeight: 800, color: colors.white, mb: 2, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: -0.5 }}>
            Why Choose Diamond?
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ mb: 6, color: colors.premiumGold, fontSize: '1.2rem', fontWeight: 500 }}>
            Experience banking redefined for the modern era
          </Typography>
          
          <Grid container spacing={3} alignItems="stretch" justifyContent="center">
            {benefits.map((benefit, idx) => (
              <Zoom in timeout={500 + idx * 200} key={idx}>
                <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 2.5, 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3, 
                    bgcolor: 'rgba(26, 26, 46, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                    '&:hover': { 
                      transform: 'translateY(-8px)', 
                      boxShadow: `0 15px 30px ${colors.premiumGold}60`,
                      border: `1px solid ${colors.premiumGold}`,
                      bgcolor: 'rgba(26, 26, 46, 0.9)',
                    } 
                  }}>
                    <Avatar sx={{ 
                      bgcolor: colors.premiumGold, 
                      color: colors.navy, 
                      width: 60, 
                      height: 60, 
                      mx: 'auto', 
                      mb: 1.5,
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'rotate(360deg)',
                      }
                    }}>
                      {benefit.icon}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: colors.white, mb: 1, fontSize: '1rem' }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.white, opacity: 0.8, flexGrow: 1, lineHeight: 1.5, fontSize: '0.8rem' }}>
                      {benefit.desc}
                    </Typography>
                  </Card>
                </Grid>
              </Zoom>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Card Types Section - 3 cards in one row with reduced size */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" textAlign="center" sx={{ fontWeight: 800, color: colors.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' }, letterSpacing: -0.5 }}>
          Premium Card Portfolio
        </Typography>
        <Typography variant="body1" textAlign="center" sx={{ mb: 5, color: colors.premiumGold, fontSize: '1.1rem', fontWeight: 500 }}>
          Three distinct options tailored to your lifestyle
        </Typography>
        
        <Grid container spacing={3} alignItems="stretch">
          {[
            { icon: <CreditCardIcon sx={{ fontSize: 32 }} />, title: "DEBIT CARDS", desc: "Access your funds instantly, anywhere in the world with zero monthly fees.", features: ["Instant Access", "Zero Fees", "Global ATM"] },
            { icon: <PaymentsIcon sx={{ fontSize: 32 }} />, title: "CREDIT CARDS", desc: "Enjoy unparalleled convenience with premium rewards and travel benefits.", features: ["Reward Points", "Travel Insurance", "Premium Support"] },
            
          ].map((card, idx) => (
            <Grid item xs={12} md={4} key={idx} sx={{ display: 'flex' }}>
              <Zoom in timeout={400 + idx * 200}>
                <Card sx={{ 
                  textAlign: 'center', 
                  p: 2.5, 
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3, 
                  position: 'relative', 
                  overflow: 'hidden', 
                  borderTop: `3px solid ${colors.premiumGold}`,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                  '&:hover': { 
                    transform: 'translateY(-6px)', 
                    boxShadow: `0 15px 30px ${colors.premiumGold}40`,
                  } 
                }}>
                  <Box sx={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100, bgcolor: `${colors.premiumGold}0D`, borderRadius: '0 0 0 100%' }} />
                  <Avatar sx={{ bgcolor: colors.premiumGold, color: colors.navy, width: 60, height: 60, mx: 'auto', mb: 1.5, transition: 'transform 0.3s', '&:hover': { transform: 'scale(1.1)' } }}>
                    {card.icon}
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: colors.navy, fontSize: '1.1rem', letterSpacing: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1, fontSize: '0.8rem', lineHeight: 1.4 }}>
                    {card.desc}
                  </Typography>
                  <Stack direction="row" spacing={0.8} justifyContent="center" sx={{ mt: 1 }} flexWrap="wrap" gap={0.8}>
                    {card.features.map((feature, i) => (
                      <Chip key={i} label={feature} size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontSize: '0.65rem', height: 24, fontWeight: 600 }} />
                    ))}
                  </Stack>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section - Enhanced Glassmorphism */}
      <Box sx={{ bgcolor: colors.premiumDark, py: 8 }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ 
            p: 5, 
            borderRadius: 4, 
            textAlign: 'center', 
            bgcolor: 'rgba(26, 26, 46, 0.85)',
            backdropFilter: 'blur(15px)',
            border: `1px solid ${colors.premiumGold}40`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 120,
              height: 120,
              bgcolor: `${colors.premiumGold}1A`,
              borderRadius: '50%',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -50,
              left: -50,
              width: 120,
              height: 120,
              bgcolor: `${colors.premiumGold}1A`,
              borderRadius: '50%',
            }
          }}>
            <DiamondIcon sx={{ fontSize: 50, color: colors.premiumGold, mb: 1.5, position: 'relative', zIndex: 1 }} />
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, color: colors.premiumGold, fontSize: { xs: '1.4rem', md: '2rem' }, position: 'relative', zIndex: 1 }}>
              Get the Card that fits you
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 2.5, color: colors.white, opacity: 0.9, position: 'relative', zIndex: 1, fontSize: '1rem' }}>
              Ready to bank your way? Receive your card in 14 business days
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
              <Chip label="✓ No minimum balance" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 700, fontSize: '0.75rem', p: 1 }} />
              <Chip label="✓ No activation fee" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 700, fontSize: '0.75rem', p: 1 }} />
              <Chip label="✓ No credit check" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 700, fontSize: '0.75rem', p: 1 }} />
            </Stack>
            
            <Typography variant="h4" sx={{ color: colors.premiumGold, fontWeight: 800, mb: 3, fontSize: '1.5rem', position: 'relative', zIndex: 1 }}>
              Only $50 One-Time Fee
            </Typography>
            
            <Button 
              variant="contained" 
              sx={{ 
                bgcolor: colors.premiumGold,
                color: colors.navy,
                fontWeight: 800, 
                px: 5, 
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 50,
                boxShadow: `0 4px 15px ${colors.premiumGold}60`,
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                  bgcolor: colors.lightGold,
                  transform: 'translateY(-3px)',
                  boxShadow: `0 8px 25px ${colors.premiumGold}80`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Order Your Diamond Card
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Where We Work - Enhanced Gallery */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" textAlign="center" sx={{ fontWeight: 800, color: colors.navy, mb: 1, fontSize: { xs: '1.8rem', md: '2.5rem' }, letterSpacing: -0.5 }}>
          WHERE WE WORK
        </Typography>
        <Typography variant="h5" textAlign="center" sx={{ color: colors.premiumGold, fontWeight: 600, mb: 5, fontSize: '1.1rem', letterSpacing: 2 }}>
          OUR OFFICES
        </Typography>
        
        <Grid container spacing={2.5}>
          {officeImages.map((img, idx) => (
            <Grid item xs={6} sm={4} md={3} key={idx}>
              <Paper 
                sx={{ 
                  overflow: 'hidden', 
                  borderRadius: 2,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  '&:hover': { 
                    transform: 'translateY(-4px) scale(1.02)', 
                    boxShadow: `0 12px 25px ${colors.premiumGold}50`,
                  }
                }}
              >
                <Box
                  component="img"
                  src={img}
                  alt={`Office ${idx + 1}`}
                  sx={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover',
                    transition: 'transform 0.4s',
                    '&:hover': {
                      transform: 'scale(1.08)',
                    }
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* News Section - Enhanced with better cards */}
      <Box sx={{ bgcolor: colors.premiumDark, py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" sx={{ color: colors.premiumGold, mb: 2, fontWeight: 800, letterSpacing: 3, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              DIAMOND CARDS NEWS
            </Typography>
            <Typography variant="body1" sx={{ color: colors.white, opacity: 0.8, maxWidth: 600, mx: 'auto', fontSize: '1rem' }}>
              Keeping you updated with the latest financial insights
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {newsItems.map((item, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Zoom in timeout={300 + idx * 150}>
                  <Paper sx={{ 
                    overflow: 'hidden',
                    bgcolor: 'rgba(26, 26, 46, 0.9)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: 3, 
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                    height: '100%',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': { 
                      transform: 'translateY(-6px)', 
                      boxShadow: `0 20px 40px ${colors.premiumGold}30`, 
                      border: `1px solid ${colors.premiumGold}` 
                    }
                  }}>
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.title}
                      sx={{
                        width: { xs: '100%', sm: 180 },
                        height: { xs: 160, sm: 'auto' },
                        objectFit: 'cover',
                        transition: 'transform 0.4s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    />
                    <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 14, color: colors.premiumGold, mr: 0.8 }} />
                        <Typography variant="caption" sx={{ color: colors.premiumGold, fontWeight: 500 }}>
                          {item.date}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ color: colors.white, fontWeight: 700, mb: 1.5, fontSize: '1rem', lineHeight: 1.4 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: `${colors.white}99`, lineHeight: 1.6, mb: 2, flex: 1, fontSize: '0.8rem' }}>
                        {item.excerpt.substring(0, 120)}...
                      </Typography>
                      <Button 
                        sx={{ 
                          color: colors.premiumGold, 
                          textTransform: 'none', 
                          fontWeight: 700, 
                          p: 0, 
                          alignSelf: 'flex-start',
                          fontSize: '0.85rem',
                          '&:hover': { 
                            transform: 'translateX(6px)', 
                            color: colors.lightGold,
                          }, 
                          transition: 'all 0.3s' 
                        }}
                        endIcon={<ArrowForwardIcon />}
                      >
                        Read More
                      </Button>
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Get in Touch Section - Enhanced Form */}
      <Box sx={{ bgcolor: colors.premiumDark, py: 10, borderTop: `1px solid ${colors.premiumGold}40` }} id="contact">
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" sx={{ color: colors.premiumGold, mb: 1, fontWeight: 700, letterSpacing: 4, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
              DIAMOND NETWORK
            </Typography>
            <Typography variant="h2" sx={{ color: colors.white, mb: 2, fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, letterSpacing: -0.5 }}>
              GET IN TOUCH
            </Typography>
            <Typography variant="body1" sx={{ color: colors.white, opacity: 0.8, maxWidth: 500, mx: 'auto', fontSize: '1rem' }}>
              Feel free to make contact with any questions you may have. We will get back to you within 24 hours.
            </Typography>
          </Box>
          
          <Paper component="form" onSubmit={handleContactSubmit} sx={{ 
            p: 4, 
            bgcolor: 'rgba(26, 26, 46, 0.85)',
            backdropFilter: 'blur(15px)',
            borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.15)',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: `${colors.premiumGold}60`,
              boxShadow: `0 10px 30px ${colors.premiumGold}20`,
            }
          }}>
            <Stack spacing={3}>
              <TextField 
                label="Name*" 
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth 
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    color: colors.white, 
                    '& fieldset': { borderColor: `${colors.white}33` }, 
                    '&:hover fieldset': { borderColor: colors.premiumGold },
                    '&.Mui-focused fieldset': { borderColor: colors.premiumGold, borderWidth: 2 }
                  },
                  '& .MuiInputLabel-root': { color: `${colors.white}99`, '&.Mui-focused': { color: colors.premiumGold } }
                }}
              />
              <TextField 
                label="Email*" 
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth 
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    color: colors.white, 
                    '& fieldset': { borderColor: `${colors.white}33` }, 
                    '&:hover fieldset': { borderColor: colors.premiumGold },
                    '&.Mui-focused fieldset': { borderColor: colors.premiumGold, borderWidth: 2 }
                  },
                  '& .MuiInputLabel-root': { color: `${colors.white}99`, '&.Mui-focused': { color: colors.premiumGold } }
                }}
              />
              <TextField 
                label="Phone" 
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth 
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    color: colors.white, 
                    '& fieldset': { borderColor: `${colors.white}33` }, 
                    '&:hover fieldset': { borderColor: colors.premiumGold },
                    '&.Mui-focused fieldset': { borderColor: colors.premiumGold, borderWidth: 2 }
                  },
                  '& .MuiInputLabel-root': { color: `${colors.white}99`, '&.Mui-focused': { color: colors.premiumGold } }
                }}
              />
              <TextField 
                label="Message" 
                name="message"
                value={formData.message}
                onChange={handleChange}
                multiline 
                rows={3}
                fullWidth 
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    color: colors.white, 
                    '& fieldset': { borderColor: `${colors.white}33` }, 
                    '&:hover fieldset': { borderColor: colors.premiumGold },
                    '&.Mui-focused fieldset': { borderColor: colors.premiumGold, borderWidth: 2 }
                  },
                  '& .MuiInputLabel-root': { color: `${colors.white}99`, '&.Mui-focused': { color: colors.premiumGold } }
                }}
              />
              <Button 
                type="submit"
                variant="contained" 
                fullWidth 
                size="large"
                endIcon={<SendIcon />}
                sx={{ 
                  bgcolor: colors.premiumGold,
                  color: colors.navy,
                  fontWeight: 800, 
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 50,
                  boxShadow: `0 4px 15px ${colors.premiumGold}60`,
                  '&:hover': { 
                    bgcolor: colors.lightGold, 
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 25px ${colors.premiumGold}80`,
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Send Message
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* Social Media Footer */}
      <Box sx={{ bgcolor: colors.premiumDark, py: 3, borderTop: `1px solid ${colors.premiumGold}33` }}>
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mb: 2 }}>
            <IconButton sx={{ color: colors.premiumGold, '&:hover': { transform: 'translateY(-3px)', color: colors.lightGold }, transition: 'all 0.3s' }}>
              <FacebookIcon />
            </IconButton>
            <IconButton sx={{ color: colors.premiumGold, '&:hover': { transform: 'translateY(-3px)', color: colors.lightGold }, transition: 'all 0.3s' }}>
              <TwitterIcon />
            </IconButton>
            <IconButton sx={{ color: colors.premiumGold, '&:hover': { transform: 'translateY(-3px)', color: colors.lightGold }, transition: 'all 0.3s' }}>
              <LinkedInIcon />
            </IconButton>
            <IconButton sx={{ color: colors.premiumGold, '&:hover': { transform: 'translateY(-3px)', color: colors.lightGold }, transition: 'all 0.3s' }}>
              <InstagramIcon />
            </IconButton>
          </Stack>
          <Typography variant="body2" sx={{ color: `${colors.white}60`, textAlign: 'center', fontSize: '0.75rem' }}>
            © 2024 Diamond Card Corporation. All rights reserved.
          </Typography>
        </Container>
      </Box>

      {/* Diamond Network Footer Bar - Premium Gold */}
      <Box sx={{ bgcolor: colors.premiumGold, py: 3, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: colors.navy, letterSpacing: 5, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
          DIAMOND NETWORK
        </Typography>
        <Typography variant="caption" sx={{ color: colors.navy, opacity: 0.7, mt: 0.5, display: 'block', letterSpacing: 2 }}>
          Connecting global financial excellence
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ bgcolor: colors.navy, color: colors.premiumGold, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home;